// ** react imports
import React from 'react';
import PropTypes from 'prop-types';

// ** enums imports
import { USER_ROLES } from '../../../../enums/common';

const TimelineStepper = ({
  items,
  formatDate,
  getEventIcon,
  getActorName,
  className = '',
}) => {
  const renderValue = (value, level = 0) => {
    if (value === null || value === undefined) {
      return (
        <span className="italic text-gray-600 dark:text-gray-400">
          No value
        </span>
      );
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return (
          <span className="italic text-gray-600 dark:text-gray-400">
            Empty list
          </span>
        );
      }

      // Check if it's an array of objects (complex nested data)
      if (typeof value[0] === 'object' && value[0] !== null) {
        return (
          <div
            className={`space-y-2 ${level > 0 ? 'ml-3 pl-3 border-l-2 border-gray-200 dark:border-gray-700' : ''}`}
          >
            {value.map((item, idx) => (
              <div
                key={`${item}-${idx}`}
                className="p-3 bg-white dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700/50"
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-semibold bg-sky-500 text-white rounded-full">
                    {idx + 1}
                  </span>
                  <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    Item {idx + 1}
                  </span>
                </div>
                {renderNestedObject(item, level + 1)}
              </div>
            ))}
          </div>
        );
      }

      // Simple array of primitives
      return (
        <div className="flex flex-wrap gap-2">
          {value.map((item, idx) => (
            <span
              key={idx}
              className="inline-flex items-center px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 rounded border border-gray-200 dark:border-gray-600"
            >
              {String(item)}
            </span>
          ))}
        </div>
      );
    }

    // Handle objects
    if (typeof value === 'object') {
      return renderNestedObject(value, level);
    }

    // Handle primitives (string, number, boolean)
    return (
      <span className="text-gray-70 dark:text-gray-300">{String(value)}</span>
    );
  };

  const renderNestedObject = (obj, level = 0) => {
    if (!obj || typeof obj !== 'object') return null;

    const entries = Object.entries(obj);
    if (entries.length === 0) {
      return (
        <span className="italic text-gray-400 dark:text-gray-600">
          Empty object
        </span>
      );
    }

    return (
      <div
        className={`space-y-2 ${level > 0 ? 'ml-3 pl-3 border-l-2 border-gray-200 dark:border-gray-700' : ''}`}
      >
        {entries.map(([key, value]) => {
          // Format the key to be more readable
          const formattedKey = key
            .replace(/_/g, ' ')
            .replace(/([A-Z])/g, ' $1')
            .trim()
            .split(' ')
            .map(
              word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            )
            .join(' ');

          return (
            <div key={key} className="text-xs">
              <div className="flex items-start gap-2">
                <span className="font-semibold text-gray-800 dark:text-gray-200 min-w-fit">
                  {formattedKey}:
                </span>
                <div className="flex-1 min-w-0">
                  {renderValue(value, level + 1)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderAnswer = answer => {
    // Try to parse if it's a JSON string
    let parsedAnswer = answer;
    if (typeof answer === 'string') {
      try {
        parsedAnswer = JSON.parse(answer);
      } catch (e) {
        // If parsing fails, it's a regular string
        // Log the parsing error for debugging purposes
        console.debug('Failed to parse answer as JSON:', e.message);
        return (
          <span className="text-gray-700 dark:text-gray-300">{answer}</span>
        );
      }
    }

    return renderValue(parsedAnswer);
  };

  const defaultFormatDate = dateString => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800)
      return `${Math.floor(diffInSeconds / 86400)}d ago`;

    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const defaultGetActorName = actor => {
    if (!actor) return 'System';
    return actor.fullName || actor.email || 'Unknown User';
  };

  const formatFn = formatDate || defaultFormatDate;
  const getActorFn = getActorName || defaultGetActorName;

  const getRoleBadge = role => {
    switch (role) {
      case USER_ROLES.ADMIN:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400">
            Administrator
          </span>
        );
      case USER_ROLES.MANAGER:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
            Manager
          </span>
        );
      case USER_ROLES.CUSTOMER:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
            Customer
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
            {role || 'Unknown'}
          </span>
        );
    }
  };

  return (
    <ol className={`relative ${className}`} aria-label="Activity timeline">
      {items ? (
        <div className="space-y-6 w-8/12">
          <div
            className="absolute left-[19px] top-0 bottom-0 w-[2px] bg-gradient-to-b from-sky-400/60 via-sky-30/30 to-transparent dark:from-sky-500/50 dark:via-sky-600/20 dark:to-transparent"
            aria-hidden="true"
          ></div>

          {items.map((item, index) => {
            const isFirst = index === 0;

            return (
              <li key={item?._id || index} className="relative flex gap-4 ">
                {/* Timeline dot/icon with theme support */}
                <div className="relative flex-shrink-0 z-10">
                  <div
                    className={`
                    w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300
                    ${
                      isFirst
                        ? 'bg-sky-600 dark:bg-sky-500 ring-4 ring-sky-500/20 dark:ring-sky-400/20 shadow-lg shadow-sky-50/30 dark:shadow-sky-400/20'
                        : 'bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-700  shadow-sm'
                    }
                  `}
                  >
                    {getEventIcon ? (
                      <div
                        className={
                          isFirst
                            ? 'text-white text-lg'
                            : 'text-gray-600 dark:text-gray-400 text-lg transition-colors'
                        }
                      >
                        {getEventIcon(item.event)}
                      </div>
                    ) : (
                      <div
                        className={`w-2 h-2 rounded-full ${isFirst ? 'bg-white' : 'bg-gray-400 dark:bg-gray-600'}`}
                      ></div>
                    )}
                  </div>
                </div>

                {/* Content card with complete theme support */}
                <div className="flex-1 min-w-0">
                  <div
                    className={`
                    bg-white dark:bg-violet-900/30 backdrop-blur-sm rounded-lg border transition-all duration-300
                    ${
                      isFirst
                        ? 'border-sky-200 dark:border-sky-500/30 shadow-lg shadow-sky-500/5 dark:shadow-sky-400/10 bg-sky-50 dark:bg-sky-500/10'
                        : 'border-gray-200 dark:border-gray-700/50'
                    }
                  `}
                  >
                    <div className="p-4">
                      {/* Header with title and timestamp */}
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <h3 className="font-medium text-gray-900 dark:text-gray-100 text-sm leading-snug">
                            {item.message}
                          </h3>
                        </div>
                        <time
                          className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap flex-shrink-0"
                          dateTime={item.createdAt}
                        >
                          {formatFn(item.createdAt)}
                        </time>
                      </div>

                      {/* Actor info with theme support */}
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 text-xs">
                          <i
                            className="ri-user-3-line text-gray-500 dark:text-gray-400"
                            aria-hidden="true"
                          ></i>
                          <span className="text-gray-700 dark:text-gray-300">
                            {getActorFn(item.actor)}
                          </span>
                        </div>
                        {item.actor?.role && getRoleBadge(item.actor?.role)}
                      </div>

                      {/* Event-specific content with proper nested data handling */}
                      {item.event?.includes(
                        'quiz_response.section_answer_updated_by_admin'
                      ) &&
                        item.newData?.answers && (
                          <div className="mt-3 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700/50">
                            <div className="flex items-center gap-2 mb-3">
                              <i
                                className="ri-edit-line text-sky-600 dark:text-sky-400"
                                aria-hidden="true"
                              ></i>
                              <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                                Updated Answers
                              </p>
                            </div>
                            <div className="space-y-3">
                              {item.newData.answers?.map((answer, idx) => (
                                <div
                                  key={answer?._id || idx}
                                  className="p-3 bg-white dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700"
                                >
                                  <div className="mb-2">
                                    <span className="text-xs font-semibold text-gray-900 dark:text-gray-100">
                                      Q. {answer?.questionText}
                                    </span>
                                  </div>
                                  <div className="text-sm">
                                    {renderAnswer(answer?.answer)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                      {/* Additional event-specific content for other events */}
                      {(item.event === 'quiz_response.content_copied' ||
                        item.event === 'tax_file.content_copied') &&
                        item.newData?.copiedAt && (
                          <div className="mt-3 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700/50">
                            <div className="text-sm">
                              {item.newData?.copiedValues &&
                                item.newData.copiedValues.length > 0 && (
                                  <p>
                                    {Array.isArray(item.newData.copiedValues)
                                      ? item.newData.copiedValues.join(', ')
                                      : item.newData.copiedValues}
                                  </p>
                                )}
                            </div>
                          </div>
                        )}

                      {/* Additional event-specific content for other events */}
                      {item.event === 'tax_file.notes_added' && (
                        <div className="mt-3 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700/50">
                          <div className="text-sm">{item.newData.notes}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <div className="relative mb-4">
            <div className="absolute inset-0 bg-blue-500/10 dark:bg-blue-500/20 rounded-full blur-2xl"></div>
            <div className="relative w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800/50 border-2 border-gray-200 dark:border-gray-700 flex items-center justify-center">
              <i
                className="ri-history-line text-3xl text-gray-400 dark:text-gray-500"
                aria-hidden="true"
              ></i>
            </div>
          </div>
          <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-2">
            No activity yet
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 text-center max-w-sm">
            When actions are taken, they will appear here in chronological
            order.
          </p>
        </div>
      )}
    </ol>
  );
};

TimelineStepper.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      message: PropTypes.string.isRequired,
      event: PropTypes.string.isRequired,
      createdAt: PropTypes.string.isRequired,
      updatedAt: PropTypes.string,
      actor: PropTypes.shape({
        _id: PropTypes.string,
        fullName: PropTypes.string,
        email: PropTypes.string,
        role: PropTypes.string,
      }),
      newData: PropTypes.object,
      previousData: PropTypes.object,
      metadata: PropTypes.shape({
        ipAddress: PropTypes.string,
        userAgent: PropTypes.string,
        deviceType: PropTypes.string,
        os: PropTypes.string,
        browserInfo: PropTypes.string,
        appVersion: PropTypes.string,
        deviceId: PropTypes.string,
      }),
      __v: PropTypes.number,
    })
  ),
  formatDate: PropTypes.func,
  getEventIcon: PropTypes.func,
  getActorName: PropTypes.func,
};

export default TimelineStepper;
